import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './GestionClasses.css';
import DashboardHeader from '../../components/DashboardHeader';
import AdminNavigationBar from '../../components/AdminNavigationBar';

const API_BASE_URL = 'http://127.0.0.1:8000/api/v1';

function GestionClasses() {
  const [classes, setClasses] = useState([]);
  const [statistiques, setStatistiques] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingClasse, setEditingClasse] = useState(null);
  const [filterNiveau, setFilterNiveau] = useState('Tous les niveaux');
  const [formData, setFormData] = useState({
    nom: '',
    niveau: 'Lycée',
    capacite: 30
  });

  useEffect(() => {
    fetchData();
  }, [filterNiveau]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [classesRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/classes`, {
          params: filterNiveau !== 'Tous les niveaux' ? { niveau: filterNiveau } : {}
        }),
        axios.get(`${API_BASE_URL}/admin/classes/statistiques`)
      ]);

      console.log('Classes data:', classesRes.data);
      console.log('Stats data:', statsRes.data);

      setClasses(classesRes.data.data || []);
      setStatistiques(statsRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      showMessage('error', 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleOpenModal = (classe = null) => {
    if (classe) {
      setEditingClasse(classe);
      setFormData({
        nom: classe.nom,
        niveau: classe.niveau,
        capacite: classe.capacite
      });
    } else {
      setEditingClasse(null);
      setFormData({
        nom: '',
        niveau: 'Lycée',
        capacite: 30
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingClasse(null);
    setFormData({
      nom: '',
      niveau: 'Lycée',
      capacite: 30
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingClasse) {
        await axios.put(`${API_BASE_URL}/admin/classes/${editingClasse.id}`, formData);
        showMessage('success', 'Classe modifiée avec succès');
      } else {
        await axios.post(`${API_BASE_URL}/admin/classes`, formData);
        showMessage('success', 'Classe créée avec succès');
      }
      
      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', error.response?.data?.message || 'Erreur lors de l\'opération');
    }
  };

  const handleDelete = async (id, nom) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer la classe "${nom}" ?`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/admin/classes/${id}`);
      showMessage('success', 'Classe supprimée avec succès');
      fetchData();
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const getNiveauColor = (niveau) => {
    const colors = {
      'Collège': '#3498db',
      'Lycée': '#27ae60',
      'Polytechnique': '#e74c3c',
      'Formation': '#f39c12'
    };
    return colors[niveau] || '#95a5a6';
  };

  const getTauxColor = (taux) => {
    if (taux >= 90) return '#e74c3c';
    if (taux >= 75) return '#f39c12';
    if (taux >= 50) return '#27ae60';
    return '#3498db';
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <DashboardHeader />
        <AdminNavigationBar title="Gestion des Classes" />
        <div className="admin-container">
          <main className="admin-content admin-content-full">
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Chargement des classes...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Gestion des Classes" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          <div className="gestion-classes-container">
            
            {message.text && (
              <div className={`alert-message ${message.type}`}>
                <span className="alert-icon">{message.type === 'success' ? '✅' : '❌'}</span>
                <span>{message.text}</span>
              </div>
            )}

            {/* Statistiques */}
            {statistiques && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
                    🏫
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{statistiques.total_classes}</div>
                    <div className="stat-label">Classes</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f093fb, #f5576c)' }}>
                    👥
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{statistiques.total_etudiants}</div>
                    <div className="stat-label">Étudiants</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #4facfe, #00f2fe)' }}>
                    📊
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{statistiques.capacite_totale}</div>
                    <div className="stat-label">Capacité Totale</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #43e97b, #38f9d7)' }}>
                    📈
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{statistiques.taux_remplissage_global}%</div>
                    <div className="stat-label">Taux de Remplissage</div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions et Filtres */}
            <div className="actions-bar">
              <div className="filter-section">
                <label>Filtrer par niveau :</label>
                <select
                  value={filterNiveau}
                  onChange={(e) => setFilterNiveau(e.target.value)}
                  className="filter-select"
                >
                  <option value="Tous les niveaux">Tous les niveaux</option>
                  <option value="Collège">Collège</option>
                  <option value="Lycée">Lycée</option>
                  <option value="Polytechnique">Polytechnique</option>
                  <option value="Formation">Formation</option>
                </select>
              </div>
              <button className="btn-add" onClick={() => handleOpenModal()}>
                <span className="btn-icon">➕</span>
                Ajouter une Classe
              </button>
            </div>

            {/* Liste des Classes */}
            <div className="classes-grid">
              {classes.map(classe => (
                <div key={classe.id} className="classe-card">
                  <div className="classe-header">
                    <h3>{classe.nom}</h3>
                    <span 
                      className="niveau-badge" 
                      style={{ backgroundColor: getNiveauColor(classe.niveau) }}
                    >
                      {classe.niveau}
                    </span>
                  </div>
                  
                  <div className="classe-stats">
                    <div className="stat-item">
                      <span className="stat-icon">👥</span>
                      <div>
                        <div className="stat-number">{classe.effectif} / {classe.capacite}</div>
                        <div className="stat-text">Étudiants</div>
                      </div>
                    </div>
                    
                    <div className="stat-item">
                      <span className="stat-icon">📊</span>
                      <div>
                        <div 
                          className="stat-number" 
                          style={{ color: getTauxColor(classe.taux_remplissage) }}
                        >
                          {classe.taux_remplissage}%
                        </div>
                        <div className="stat-text">Remplissage</div>
                      </div>
                    </div>
                  </div>

                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ 
                        width: `${classe.taux_remplissage}%`,
                        backgroundColor: getTauxColor(classe.taux_remplissage)
                      }}
                    ></div>
                  </div>

                  <div className="classe-actions">
                    <button 
                      className="btn-edit" 
                      onClick={() => handleOpenModal(classe)}
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn-delete" 
                      onClick={() => handleDelete(classe.id, classe.nom)}
                      title="Supprimer"
                      disabled={classe.effectif > 0}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {classes.length === 0 && (
              <div className="empty-state">
                <span className="empty-icon">📚</span>
                <h3>Aucune classe trouvée</h3>
                <p>Commencez par créer votre première classe</p>
                <button className="btn-add" onClick={() => handleOpenModal()}>
                  <span className="btn-icon">➕</span>
                  Créer une Classe
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modal Ajout/Modification */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingClasse ? 'Modifier la Classe' : 'Nouvelle Classe'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>✕</button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Nom de la classe *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Ex: Seconde A, Terminale C..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Niveau *</label>
                <select
                  value={formData.niveau}
                  onChange={(e) => setFormData({ ...formData, niveau: e.target.value })}
                  required
                >
                  <option value="Collège">Collège</option>
                  <option value="Lycée">Lycée</option>
                  <option value="Polytechnique">Polytechnique</option>
                  <option value="Formation">Formation</option>
                </select>
              </div>

              <div className="form-group">
                <label>Capacité *</label>
                <input
                  type="number"
                  value={formData.capacite}
                  onChange={(e) => setFormData({ ...formData, capacite: parseInt(e.target.value) })}
                  min="1"
                  max="100"
                  required
                />
                <small>Nombre maximum d'étudiants</small>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  {editingClasse ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionClasses;
